
import { Component, Vue } from 'vue-property-decorator'
import { getConsultCalls, getConsultCallsCounts, getConsultCallCategories } from '@/api/consultCalls'

const countryOptions = [
  { key: 'tr', displayName: 'TR' },
  { key: 'gb', displayName: 'GB' },
  { key: '', displayName: 'leave empty' }
]

@Component({
  name: 'CallLogs'
})
export default class extends Vue {
  private list: any[] = []
  private totalCount = 0
  private listLoading = true
  private countryOptions = countryOptions
  private activeTab: 'answered' | 'missed' | 'failed' = 'answered'
  private counts = { answered: 0, missed: 0, failed: 0 }
  private categoryOptions: any[] = []

  private listQuery: any = {
    text: '',
    country: undefined,
    category: undefined,
    userId: undefined,
    startDate: undefined,
    endDate: undefined,
    page: 1,
    pageSize: 20
  }

  // YYYY-MM-DD in local time. el-date-picker is value-format="yyyy-MM-dd",
  // and the backend treats startDate/endDate as plain calendar days, so we
  // never want a UTC-shifted value.
  private todayYmd(): string {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }

  created() {
    // Hydrate filter state from the URL so reload + browser back keep the
    // operator's view. If no startDate/endDate is in the URL we default to
    // today — assistants almost always want "what happened in the last few
    // hours" first, and the unfiltered query was rarely what they wanted.
    const q = this.$route.query as Record<string, string>
    if (q.tab === 'missed' || q.tab === 'failed' || q.tab === 'answered') {
      this.activeTab = q.tab as any
    }
    const today = this.todayYmd()
    this.listQuery = {
      text: q.text || '',
      country: q.country || undefined,
      category: q.category || undefined,
      userId: q.userId || undefined,
      startDate: q.startDate || today,
      endDate: q.endDate || today,
      page: Number(q.page) || 1,
      pageSize: Number(q.pageSize) || 20,
    }
    this.fetchAll()
    this.fetchCategories()
  }

  private async fetchCategories() {
    try {
      const response: any = await getConsultCallCategories()
      this.categoryOptions = response.data?.data || []
    } catch (err) {
      this.categoryOptions = []
    }
  }

  private categoryName(row: any): string {
    const match = this.categoryOptions.find((c: any) => c.id === row.categoryId)
    return match ? match.name : String(row.categoryKey || '').replace(/^danis_subject_/, '')
  }

  // Push current filter+tab+page state back into the URL so reload survives.
  // Use replace() instead of push() — every keystroke shouldn't grow browser
  // history. Empty/default values are omitted to keep the URL short.
  private syncFiltersToUrl() {
    const q: Record<string, string> = {}
    if (this.activeTab !== 'answered') q.tab = this.activeTab
    if (this.listQuery.text) q.text = this.listQuery.text
    if (this.listQuery.country) q.country = this.listQuery.country
    if (this.listQuery.category) q.category = this.listQuery.category
    if (this.listQuery.userId) q.userId = this.listQuery.userId
    if (this.listQuery.startDate) q.startDate = this.listQuery.startDate
    if (this.listQuery.endDate) q.endDate = this.listQuery.endDate
    if (this.listQuery.page && this.listQuery.page > 1) q.page = String(this.listQuery.page)
    if (this.listQuery.pageSize && this.listQuery.pageSize !== 20) q.pageSize = String(this.listQuery.pageSize)
    // Comparing JSON strings — Vue Router rejects identical-route replacements
    // with NavigationDuplicated unless we catch, so just swallow.
    this.$router.replace({ query: q }).catch(() => { /* duplicate route, ignore */ })
  }

  private fetchAll() {
    this.fetchData()
    this.fetchCounts()
  }

  private async fetchData() {
    try {
      this.listLoading = true
      const response: any = await getConsultCalls({
        tab: this.activeTab,
        text: this.listQuery.text || undefined,
        country: this.listQuery.country || undefined,
        category: this.listQuery.category || undefined,
        userId: this.listQuery.userId || undefined,
        startDate: this.listQuery.startDate || undefined,
        endDate: this.listQuery.endDate || undefined,
        page: this.listQuery.page,
        pageSize: this.listQuery.pageSize
      })
      const body = response.data
      this.list = body.data || []
      this.totalCount = body.pagination?.totalCount || 0
    } catch (err) {
      this.list = []
      this.totalCount = 0
    } finally {
      this.listLoading = false
    }
  }

  private async fetchCounts() {
    try {
      const response: any = await getConsultCallsCounts({
        text: this.listQuery.text || undefined,
        country: this.listQuery.country || undefined,
        category: this.listQuery.category || undefined,
        userId: this.listQuery.userId || undefined,
        startDate: this.listQuery.startDate || undefined,
        endDate: this.listQuery.endDate || undefined
      })
      const body = response.data
      this.counts = body.data || { answered: 0, missed: 0, failed: 0 }
    } catch (err) {
      this.counts = { answered: 0, missed: 0, failed: 0 }
    }
  }

  private handleTabChange() {
    this.listQuery.page = 1
    this.syncFiltersToUrl()
    this.fetchData()
  }

  private handleFilter() {
    this.listQuery.page = 1
    this.syncFiltersToUrl()
    this.fetchAll()
  }

  // "Sıfırla" — clear filters back to today's records, not all-time. Matching
  // the page's load default avoids the surprise where reset returns a giant
  // unfiltered list that the operator didn't ask for.
  private resetFilters() {
    const today = this.todayYmd()
    this.listQuery = {
      text: '',
      country: undefined,
      category: undefined,
      userId: undefined,
      startDate: today,
      endDate: today,
      page: 1,
      pageSize: this.listQuery.pageSize
    }
    this.syncFiltersToUrl()
    this.fetchAll()
  }

  private handleCurrentChange(newPage: number) {
    this.listQuery.page = newPage
    this.syncFiltersToUrl()
    this.fetchData()
  }

  private handleSizeChange(newPageSize: number) {
    this.listQuery.pageSize = newPageSize
    this.listQuery.page = 1
    this.syncFiltersToUrl()
    this.fetchData()
  }

  private formatDate(value: string | null) {
    if (!value) return '—'
    const d = new Date(value)
    if (isNaN(d.getTime())) return '—'
    return d.toLocaleString('tr-TR', { hour12: false })
  }

  private callStatusType(status: string) {
    if (!status) return 'info'
    if (status === 'completed') return 'success'
    if (status === 'user-canceled' || status === 'assistant-canceled') return 'warning'
    if (status === 'interrupted') return 'danger'
    return 'info'
  }

  private computeWaitSeconds(row: any): string {
    if (!row.userCallStart || !row.assistantJoinedAt) return '—'
    const start = new Date(row.userCallStart).getTime()
    const joined = new Date(row.assistantJoinedAt).getTime()
    if (isNaN(start) || isNaN(joined) || joined < start) return '—'
    return Math.round((joined - start) / 1000).toString()
  }

  // After Faz 5, assistantsNotifiedCount = "online & region-matched" (the
  // broadcast was actually attempted), and assistantCandidateCount is the
  // region-matched total. Older rows have no candidateCount — we treat
  // notified as the candidate count there to preserve the legacy display.
  private diagnosticLabel(row: any): string {
    const candidate = row.assistantCandidateCount
    const notified = row.assistantsNotifiedCount
    const panelAcked = row.panelAckedCount
    const joined = Number(row.joinEventCount || 0)

    // Modern path (Faz 5+): candidateCount is present.
    if (candidate !== null && candidate !== undefined) {
      const c = Number(candidate)
      const n = Number(notified ?? 0)
      const a = Number(panelAcked || 0)
      if (c === 0) return this.$t('callEvents.diagNoRegionAssistant') as string
      if (n === 0) return this.$t('callEvents.diagCandidatesNoneOnline', { c }) as string
      if (a === 0) return this.$t('callEvents.diagConnectedPanelFailed', { n, c }) as string
      if (joined === 0) return this.$t('callEvents.diagPanelsSawNobodyOpened', { a, n }) as string
      if (joined === 1) return this.$t('callEvents.diagPanelsSawNotAnswered', { a, n }) as string
      return this.$t('callEvents.diagPanelsSawJoins', { a, joined }) as string
    }

    // Faz 2-4 calls: only the old assistantsNotifiedCount (= region-matched).
    if (notified !== null && notified !== undefined) {
      const n = Number(notified)
      if (n === 0) return this.$t('callEvents.diagNoRegionAssistant') as string
      if (joined === 0) return this.$t('callEvents.diagSentNoneConnected', { n }) as string
      if (joined === 1) return this.$t('callEvents.diagSentNotAnswered', { n }) as string
      return this.$t('callEvents.diagNotifiedJoins', { n, joined }) as string
    }

    // Faz 2 öncesi — sadece Zoom join sayısı
    if (joined === 0) return this.$t('callEvents.diagLegacyNoJoins') as string
    if (joined === 1) return this.$t('callEvents.diagLegacyOneSide') as string
    return this.$t('callEvents.diagLegacyJoins', { joined }) as string
  }

  private diagnosticType(row: any): string {
    const candidate = row.assistantCandidateCount
    const notified = row.assistantsNotifiedCount
    const panelAcked = row.panelAckedCount
    const joined = Number(row.joinEventCount || 0)

    if (candidate !== null && candidate !== undefined) {
      const c = Number(candidate)
      const n = Number(notified ?? 0)
      const a = Number(panelAcked || 0)
      if (c === 0) return 'danger'
      if (n === 0) return 'danger'        // candidates existed but none online
      if (a === 0) return 'danger'        // online but panel didn't render
      if (joined === 0) return 'warning'  // panel saw, nobody opened
      return 'info'
    }

    if (notified !== null && notified !== undefined) {
      const n = Number(notified)
      if (n === 0) return 'danger'
      if (joined === 0) return 'warning'
      return 'info'
    }
    if (joined === 0) return 'danger'
    if (joined === 1) return 'warning'
    return 'info'
  }
}
